import React from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import toast from "react-hot-toast";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import AuthLayout from "../components/layout/AuthLayout";
import PasswordInput from "../components/PasswordInput";
import { OTPInput, Button } from "../components/ui";

const ResetPasswordSchema = Yup.object().shape({
  otp: Yup.string()
    .required("OTP is required")
    .min(6, "OTP must be at least 6 characters")
    .max(6, "OTP must be at most 6 characters"),
  newPassword: Yup.string()
    .required("New password is required")
    .min(8, "Password must be at least 8 characters")
    .matches(
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/,
      "Password must contain at least one uppercase, one lowercase, one number and one special character",
    ),
  confirmPassword: Yup.string()
    .required("Please confirm your password")
    .oneOf([Yup.ref("newPassword"), null], "Passwords must match"),
});

const ResetPassword = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { resetPassword } = useAuth();
  const email = location.state?.email;

  if (!email) {
    navigate("/forgot-password");
    return null;
  }

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      await resetPassword(email, values.otp, values.newPassword);
      toast.success("Password reset successfully");
      navigate("/login");
    } catch (error) {
      toast.error(error.message || "Failed to reset password");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AuthLayout>
      <div className="space-y-8">
        <div>
          <h2 className="font-heading text-4xl font-bold text-gray-900">
            Reset your password
          </h2>
          <p className="text-sm text-gray-500 mt-3">
            Enter the verification code sent to {email} and your new password.
          </p>
        </div>

        <Formik
          initialValues={{ otp: "", newPassword: "", confirmPassword: "" }}
          validationSchema={ResetPasswordSchema}
          onSubmit={handleSubmit}
        >
          {({
            errors,
            touched,
            isSubmitting,
            values,
            setFieldValue,
            handleBlur,
          }) => (
            <Form className="space-y-5">
              <OTPInput
                name="otp"
                value={values.otp}
                onChange={(e) => setFieldValue("otp", e.target.value)}
                onBlur={handleBlur}
                error={errors.otp && touched.otp ? errors.otp : undefined}
              />

              <div>
                <label
                  htmlFor="newPassword"
                  className="block text-sm font-semibold text-gray-700 mb-2"
                >
                  New password <span className="text-red-500">*</span>
                </label>
                <Field name="newPassword">
                  {({ field }) => (
                    <PasswordInput
                      id="newPassword"
                      {...field}
                      autoComplete="new-password"
                      placeholder="Min. 8 characters"
                      error={
                        errors.newPassword && touched.newPassword
                          ? errors.newPassword
                          : undefined
                      }
                    />
                  )}
                </Field>
              </div>

              <div>
                <label
                  htmlFor="confirmPassword"
                  className="block text-sm font-semibold text-gray-700 mb-2"
                >
                  Confirm password <span className="text-red-500">*</span>
                </label>
                <Field name="confirmPassword">
                  {({ field }) => (
                    <PasswordInput
                      id="confirmPassword"
                      {...field}
                      autoComplete="new-password"
                      placeholder="Confirm new password"
                      error={
                        errors.confirmPassword && touched.confirmPassword
                          ? errors.confirmPassword
                          : undefined
                      }
                    />
                  )}
                </Field>
              </div>

              <Button
                type="submit"
                fullWidth
                loading={isSubmitting}
                disabled={isSubmitting}
                className="py-3"
              >
                Reset password
              </Button>

              <p className="text-center text-sm text-gray-500">
                <Link
                  to="/login"
                  className="font-medium text-primary hover:text-primary-dark"
                >
                  Back to login
                </Link>
              </p>
            </Form>
          )}
        </Formik>
      </div>
    </AuthLayout>
  );
};

export default ResetPassword;
